import React from 'react';
import * as Sentry from '@sentry/react';
import {
  useRouteError,
  isRouteErrorResponse,
  useLocation,
} from 'react-router-dom';
import { CrashScreen } from '@quickmd-llc/design-system';

const normalize = (e: unknown) => {
  if (e instanceof Error) return e;
  if (isRouteErrorResponse(e))
    return new Error(
      `${e.status} ${e.statusText} ${e.data ? JSON.stringify({ data: e.data }) : ''}`,
    );
  try {
    return new Error(typeof e === 'string' ? e : JSON.stringify(e));
  } catch {
    return new Error('Unknown error (captured in router)');
  }
};

export function RouteError() {
  const routeError = useRouteError();
  // Memoize so a re-render doesn't construct a new Error identity, which would
  // otherwise re-trigger the capture effect (and StrictMode double-fires).
  const error = React.useMemo(() => normalize(routeError), [routeError]);
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development') return;
    Sentry.withScope((scope) => {
      scope.setTag('route.path', pathname);
      scope.setContext('react-router', { pathname: pathname });
      Sentry.captureException(error);
    });
  }, [error, pathname]);

  return <CrashScreen error={error} linkProps={{ href: '/' }} />;
}
